// DeviceDetails - per-device dossier in the expanded device row.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertCircle,
    AlertTriangle,
    Archive,
    Check,
    MapPin,
    MapPinOff,
    MonitorSmartphone,
    ShieldCheck,
    X,
} from 'lucide-react';
import type { DeviceDetailsProps, DeviceStatus } from './../../../types/DeviceListTypes';
import { type CommandAck, type CommandDdbItem, type CommandRow } from './../../../types/Commands';
import type { S3ObjectInfo } from './../../../types/AWSTypes';
import { useAuth } from '../../context/AuthContext';
import { formatUserIdentityDisplay, formatApiDateTime, cls, nonEmptyOr } from '../../helpers/helpers';
import RefreshButton from '../shared/RefreshButton';
import CyclingLoader from '../shared/CyclingLoader';
import DeviceConfigureForm from '../DeviceConfigureForm/DeviceConfigureForm';
import { DetailCard, DetailKeyValue } from './details/DetailCard';
import { DeviceStatPills } from './details/DeviceStatPills';
import { DeviceLogsTable } from './details/DeviceLogsTable';
import LogPreviewDialog from '../LogViewer/dialogs/LogPreviewDialog';
import ConfirmDialog from '../ConfirmDialog';
import { resolveDeviceMappingId, deviceMappingColumnLabel } from '../../helpers/deviceMappingRows';
import { formatDeviceFeedbackReason } from '../DeviceListView/utils/deviceListHelpers';

type AssignmentDetailsRow = {
    eventId: string;
    /** Race scope for this device row (from `race_devices` / event-devices). */
    raceId?: string;
    /** Resolved from `listEventRaces` when `raceId` is set. */
    raceName?: string;
    /** Race row status (e.g. PACKAGING, ACTIVE), from `listEventRaces` when `raceId` is set. */
    raceStatus?: string;
    name?: string;
    status?: string;
    assignedAthleteId?: string;
    assignedAt?: number;
    assignedBy?: string;
    recalled?: 0 | 1;
    /** True when this row is the live `event-devices` assignment (from bulk query + enrichment), not history-only. */
    isLiveAssignment?: boolean;
};

function normalizeRecalledFlag(raw: unknown): 0 | 1 | undefined {
    if (raw === 1 || raw === '1') return 1;
    if (raw === 0 || raw === '0') return 0;
    return undefined;
}

function assignmentMergeKey(r: { eventId: string; raceId?: string }): string {
    return `${String(r.eventId).trim()}\t${String(r.raceId ?? '').trim()}`;
}

function assignmentFromHistoryRecord(raw: Record<string, unknown>): AssignmentDetailsRow {
    const eventId = String(raw.eventId ?? '').trim();
    const raceIdRaw = raw.raceId != null ? String(raw.raceId).trim() : '';
    const raceId = raceIdRaw.length > 0 ? raceIdRaw : undefined;
    const raceStatusRaw = raw.raceStatus != null ? String(raw.raceStatus).trim() : '';
    const raceStatus = raceStatusRaw.length > 0 ? raceStatusRaw : undefined;
    const name = typeof raw.name === 'string' ? raw.name : undefined;
    const status = typeof raw.status === 'string' ? raw.status : undefined;
    let assignedAt: number | undefined;
    if (typeof raw.assignedAt === 'number') assignedAt = raw.assignedAt;
    else if (raw.assignedAt != null && Number.isFinite(Number(raw.assignedAt))) assignedAt = Number(raw.assignedAt);
    const assignedBy = typeof raw.assignedBy === 'string' ? raw.assignedBy : undefined;
    const assignedAthleteId =
        typeof raw.assignedAthleteId === 'string' ? raw.assignedAthleteId : undefined;
    const recalled = normalizeRecalledFlag(raw.recalled);
    return { eventId, raceId, raceStatus, name, status, assignedAt, assignedBy, assignedAthleteId, recalled };
}

/** One row per event + race scope: history first, then live enrichment overwrites. */
function mergeAssignmentsByKey(
    historyRows: AssignmentDetailsRow[],
    liveRows: AssignmentDetailsRow[],
): AssignmentDetailsRow[] {
    const byKey = new Map<string, AssignmentDetailsRow>();
    for (const h of historyRows) {
        if (h.eventId) byKey.set(assignmentMergeKey(h), { ...h });
    }
    for (const l of liveRows) {
        if (!l.eventId) continue;
        const k = assignmentMergeKey(l);
        const prev = byKey.get(k);
        byKey.set(k, { ...prev, ...l });
    }
    return Array.from(byKey.values());
}

type RaceMeta = { name?: string; status?: string };

async function attachRaceMetadata(rows: AssignmentDetailsRow[]): Promise<AssignmentDetailsRow[]> {
    const eventToRaceIds = new Map<string, Set<string>>();
    for (const r of rows) {
        const rid = String(r.raceId ?? '').trim();
        if (!rid) continue;
        const eid = String(r.eventId ?? '').trim();
        if (!eid) continue;
        if (!eventToRaceIds.has(eid)) eventToRaceIds.set(eid, new Set());
        eventToRaceIds.get(eid)!.add(rid);
    }
    if (eventToRaceIds.size === 0) return rows;

    const pairToMeta = new Map<string, RaceMeta>();
    const listRaces = window.eventsAPI?.listEventRaces;
    if (typeof listRaces !== 'function') return rows;

    for (const [eventId, raceSet] of eventToRaceIds) {
        const res = await listRaces(eventId);
        if (!res?.ok) continue;
        const races =
            (res.data as { races?: Array<{ raceId?: string; name?: string; status?: string }> } | undefined)?.races ??
            [];
        for (const race of races) {
            const rid = String(race.raceId ?? '').trim();
            if (!raceSet.has(rid)) continue;
            const nm = typeof race.name === 'string' ? race.name.trim() : '';
            const st = typeof race.status === 'string' ? race.status.trim() : '';
            pairToMeta.set(`${eventId}\t${rid}`, {
                name: nm || undefined,
                status: st || undefined,
            });
        }
    }
    if (pairToMeta.size === 0) return rows;

    return rows.map((r) => {
        const rid = String(r.raceId ?? '').trim();
        const eid = String(r.eventId ?? '').trim();
        if (!rid || !eid) return r;
        const meta = pairToMeta.get(`${eid}\t${rid}`);
        if (!meta) return r;
        return {
            ...r,
            raceName: meta.name ?? r.raceName,
            raceStatus: meta.status ?? r.raceStatus,
        };
    });
}

/** Live `event-devices` rows first, then newest by assigned time. */
function sortAssignmentsForDisplay(rows: AssignmentDetailsRow[]): AssignmentDetailsRow[] {
    return [...rows].sort((a, b) => {
        const aLive = a.isLiveAssignment ? 1 : 0;
        const bLive = b.isLiveAssignment ? 1 : 0;
        if (aLive !== bLive) return bLive - aLive;
        return (b.assignedAt ?? 0) - (a.assignedAt ?? 0);
    });
}

type DeviceNoteRow = {
    deviceId: string;
    noteId: string;
    type: 'info' | 'warning' | 'error' | 'debug';
    message: string;
    createdAt: number;
    createdBy?: string;
    eventId?: string;
};

type LifecycleRow = {
    deviceId: string;
    eventId?: string;
    raceId?: string;
    lost?: boolean;
    decommissioned?: boolean;
    damaged?: boolean;
    errored?: boolean;
    recordedAt?: number;
    reason?: string;
    updatedAt?: number;
    updatedBy?: string;
};

function keyBasename(key: string): string {
    const parts = key.split('/').filter(Boolean);
    return parts[parts.length - 1] || key;
}

/**
 * Normalize numeric epoch-like values for UI formatting.
 * API rows can contain seconds, milliseconds, or microseconds.
 */
function normalizeEpochMs(raw: unknown): number | undefined {
    if (raw == null) return undefined;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return undefined;
    // microseconds -> milliseconds
    if (n > 10_000_000_000_000) return Math.floor(n / 1000);
    // seconds -> milliseconds
    if (n < 10_000_000_000) return Math.floor(n * 1000);
    // already milliseconds
    return Math.floor(n);
}

function isFileObject(o: S3ObjectInfo): boolean {
    return !!o.key && !o.key.endsWith('/');
}

type LifecycleIcon = React.ComponentType<{ className?: string; 'aria-hidden'?: boolean; strokeWidth?: number }>;

/** One lifecycle boolean as a pill (same visual language as assignment status chips). */
function LifecycleFlagPill(props: {
    label: string;
    active: boolean;
    activeClass: string;
    inactiveClass: string;
    ActiveIcon: LifecycleIcon;
    InactiveIcon: LifecycleIcon;
}): React.JSX.Element {
    const { label, active, activeClass, inactiveClass, ActiveIcon, InactiveIcon } = props;
    const Icon = active ? ActiveIcon : InactiveIcon;
    return (
        <span
            className={cls(
                'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                active ? activeClass : inactiveClass,
            )}
            title={`${label}: ${active ? 'flagged' : 'not flagged'}`}
        >
            <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden strokeWidth={2.25} />
            <span>{label}</span>
            <span className="font-bold tabular-nums opacity-90">{active ? 'Yes' : 'No'}</span>
        </span>
    );
}

/** Event assignment status → badge colours (backend uses values like ACTIVE, PACKAGING, …). */
function eventStatusBadgeClass(status?: string): string {
    const s = (status ?? '').trim().toUpperCase();
    if (!s) return 'border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300';
    if (s === 'ACTIVE') {
        return 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-200';
    }
    if (s === 'DISTRIBUTION') {
        return 'border-sky-300 bg-sky-50 text-sky-900 dark:border-sky-700/60 dark:bg-sky-950/40 dark:text-sky-200';
    }
    if (s === 'RACKING') {
        return 'border-cyan-300 bg-cyan-50 text-cyan-900 dark:border-cyan-700/60 dark:bg-cyan-950/40 dark:text-cyan-200';
    }
    if (s === 'PACKAGING' || s === 'DRAFT') {
        return 'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-200';
    }
    if (s.includes('COMPLETE') || s === 'COMPLETED' || s === 'CANCELLED' || s === 'ARCHIVED') {
        return 'border-gray-300 bg-gray-100 text-gray-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200';
    }
    return 'border-violet-200 bg-violet-50 text-violet-900 dark:border-violet-700/60 dark:bg-violet-950/40 dark:text-violet-200';
}

function asCommandRow(raw: unknown): CommandRow | null {
    if (!raw || typeof raw !== 'object') return null;
    const row = raw as CommandDdbItem;
    let ack: CommandAck | null = null;
    if (typeof row.status_info === 'string' && row.status_info.trim()) {
        try {
            const parsed = JSON.parse(row.status_info);
            ack = parsed?.ack ?? null;
        } catch {
            ack = null;
        }
    }
    return { ddb: row, ack };
}

export default function DeviceDetails(props: DeviceDetailsProps) {
    const {
        meta,
        metaColumns,
        operatorScopedEventId,
        status: statusProp,
        latestCommand: latestCommandProp,
        fwLabel: fwLabelProp,
        battLabel: battLabelProp,
        raceLogLabel: raceLogLabelProp,
        debugLogLabel: debugLogLabelProp,
        lastHeardLabel: lastHeardLabelProp,
        reasonLabel: reasonLabelProp,
        userIdToDisplayName: userIdToDisplayNameProp,
        onRunDeviceValidation,
    } = props;
    const userIdToDisplayName = userIdToDisplayNameProp ?? {};
    const { isAdmin } = useAuth();
    const deviceId = resolveDeviceMappingId(meta as Record<string, unknown>);
    const scopedEventId = String(operatorScopedEventId ?? '').trim();
    const effectiveLogEventId = !isAdmin && scopedEventId ? scopedEventId : undefined;

    const [command, setCommand] = useState<CommandRow | null>(null);
    const [latestStatus, setLatestStatus] = useState<DeviceStatus | undefined>(undefined);
    const [coreLoading, setCoreLoading] = useState(false);
    const [coreError, setCoreError] = useState<string | null>(null);

    const [raceLogs, setRaceLogs] = useState<S3ObjectInfo[]>([]);
    const [debugLogs, setDebugLogs] = useState<S3ObjectInfo[]>([]);
    const [logsLoading, setLogsLoading] = useState<'race' | 'debug' | null>(null);
    const [logsError, setLogsError] = useState<{ race?: string; debug?: string }>({});
    const [deleteLogConfirm, setDeleteLogConfirm] = useState<{ kind: 'race' | 'debug'; obj: S3ObjectInfo } | null>(null);
    const [deletingLogKey, setDeletingLogKey] = useState<string | null>(null);

    const [tlsProvisioned, setTlsProvisioned] = useState<boolean | null>(null);
    const [tlsLoading, setTlsLoading] = useState(false);
    const [tlsError, setTlsError] = useState<string | null>(null);

    const [lifecycle, setLifecycle] = useState<LifecycleRow | null>(null);
    const [lifecycleLoading, setLifecycleLoading] = useState(false);
    const [lifecycleError, setLifecycleError] = useState<string | null>(null);

    const [attributes, setAttributes] = useState<{ raceCount?: number; waterproofedBy?: string } | null>(null);
    const [attributesLoading, setAttributesLoading] = useState(false);
    const [attributesError, setAttributesError] = useState<string | null>(null);

    const [assignments, setAssignments] = useState<AssignmentDetailsRow[]>([]);
    const [assignmentsLoading, setAssignmentsLoading] = useState(false);
    const [assignmentsError, setAssignmentsError] = useState<string | null>(null);

    const [notes, setNotes] = useState<DeviceNoteRow[]>([]);
    const [notesNextToken, setNotesNextToken] = useState<string | null>(null);
    const [notesLoading, setNotesLoading] = useState(false);
    const [notesError, setNotesError] = useState<string | null>(null);
    const [noteDialogOpen, setNoteDialogOpen] = useState(false);
    const [lifecycleDialogOpen, setLifecycleDialogOpen] = useState(false);

    const [logOpen, setLogOpen] = useState(false);
    const [activeLog, setActiveLog] = useState<S3ObjectInfo | null>(null);
    const [previewText, setPreviewText] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const cancelledRef = useRef(false);

    const propCommand = useMemo(() => asCommandRow(latestCommandProp), [latestCommandProp]);
    const effectiveCommand = useMemo(() => {
        if (!command) return propCommand;
        if (!propCommand) return command;
        const fetchedTs = typeof command.ddb.updatedAt === 'number' ? command.ddb.updatedAt : 0;
        const propTs = typeof propCommand.ddb.updatedAt === 'number' ? propCommand.ddb.updatedAt : 0;
        return propTs >= fetchedTs ? propCommand : command;
    }, [command, propCommand]);

    const effectiveStatus: DeviceStatus | undefined = latestStatus || statusProp;
    const hasEffectiveStatus = !!effectiveStatus;

    const computedFw = hasEffectiveStatus && effectiveStatus?.fw ? String(effectiveStatus.fw) : '';
    const computedBatt =
        hasEffectiveStatus && typeof effectiveStatus?.batt_mv === 'number'
            ? (effectiveStatus.batt_mv / 1000).toFixed(3)
            : '';
    const computedRaceLog =
        hasEffectiveStatus && typeof effectiveStatus?.race_log_size === 'number'
            ? String(effectiveStatus.race_log_size)
            : '';
    const computedDebugLog =
        hasEffectiveStatus && typeof effectiveStatus?.debug_log_size === 'number'
            ? String(effectiveStatus.debug_log_size)
            : '';
    const computedLastHeard =
        hasEffectiveStatus && effectiveStatus?.updatedAt != null
            ? formatApiDateTime(effectiveStatus.updatedAt, '')
            : '';
    const computedReason = hasEffectiveStatus
        ? formatDeviceFeedbackReason(effectiveStatus)
        : '';

    const modemFwLabel =
        hasEffectiveStatus && effectiveStatus?.modem_fw ? String(effectiveStatus.modem_fw) : '';
    const gnssFwLabel =
        hasEffectiveStatus && effectiveStatus?.gnss_fw ? String(effectiveStatus.gnss_fw) : '';

    const fwLabel = nonEmptyOr(fwLabelProp, computedFw);
    const battLabel = nonEmptyOr(battLabelProp, computedBatt);
    const raceLogLabel = nonEmptyOr(raceLogLabelProp, computedRaceLog);
    const debugLogLabel = nonEmptyOr(debugLogLabelProp, computedDebugLog);
    const lastHeardLabel = nonEmptyOr(lastHeardLabelProp, computedLastHeard);
    const reasonLabel = nonEmptyOr(reasonLabelProp, computedReason);

    const raceCountLabel =
        typeof attributes?.raceCount === 'number' && Number.isFinite(attributes.raceCount)
            ? String(attributes.raceCount)
            : String((meta as Record<string, unknown>).raceCount ?? '').trim();
    const waterproofedByLabel =
        String(attributes?.waterproofedBy ?? '').trim()
        || String((meta as Record<string, unknown>).waterproofedBy ?? '').trim();

    const metaPairs = useMemo(() => {
        return metaColumns.map((col) => {
            const raw = (meta as Record<string, unknown>)[col];
            const value = typeof raw === 'object' && raw !== null ? JSON.stringify(raw) : String(raw ?? '');
            return { col, value };
        });
    }, [meta, metaColumns]);

    async function refreshCore(): Promise<void> {
        if (!deviceId) return;
        try {
            setCoreLoading(true);
            setCoreError(null);
            let cmd: unknown = null;
            let st: unknown = null;

            if (!isAdmin && scopedEventId) {
                const [eventCmdRes, eventStatusRes] = await Promise.all([
                    window.eventsAPI.getEventDeviceLatestCommand(scopedEventId, deviceId),
                    window.eventsAPI.getEventDeviceStatus(scopedEventId, deviceId),
                ]);
                if (eventCmdRes?.ok === false) {
                    throw new Error(eventCmdRes?.error || 'Failed to load event device latest command');
                }
                if (eventStatusRes?.ok === false) {
                    throw new Error(eventStatusRes?.error || 'Failed to load event device status');
                }
                cmd = (eventCmdRes?.data as { command?: unknown })?.command ?? null;
                st = (eventStatusRes?.data as { status?: unknown })?.status ?? null;
            } else if (isAdmin) {
                const [deviceCmdRes, deviceStatusRes] = await Promise.all([
                    window.eventsAPI.getDeviceLatestCommand(deviceId),
                    window.eventsAPI.getDeviceStatus(deviceId),
                ]);
                if (deviceCmdRes?.ok === false) {
                    throw new Error(deviceCmdRes?.error || 'Failed to load device latest command');
                }
                if (deviceStatusRes?.ok === false) {
                    throw new Error(deviceStatusRes?.error || 'Failed to load device status');
                }
                cmd = (deviceCmdRes?.data as { command?: unknown })?.command ?? null;
                st = (deviceStatusRes?.data as { status?: unknown })?.status ?? null;
            } else {
                setCommand(null);
                setLatestStatus(undefined);
                return;
            }

            if (cancelledRef.current) return;
            setCommand(asCommandRow(cmd));
            setLatestStatus((st as DeviceStatus | null) || undefined);
        } catch (err: any) {
            if (!cancelledRef.current) {
                setCoreError(err?.message || 'Failed to refresh latest status/command');
            }
        } finally {
            if (!cancelledRef.current) setCoreLoading(false);
        }
    }

    async function loadTls(): Promise<void> {
        if (!deviceId || !window.eventsAPI?.queryDeviceTls) return;
        try {
            setTlsLoading(true);
            setTlsError(null);
            const res = await window.eventsAPI.queryDeviceTls([deviceId]);
            if (!res?.ok) {
                throw new Error(res?.error || 'Failed to check TLS status');
            }
            if (cancelledRef.current) return;
            const provisioned = (res.data as { provisioned?: Record<string, boolean> } | undefined)?.provisioned;
            if (provisioned && Object.prototype.hasOwnProperty.call(provisioned, deviceId)) {
                setTlsProvisioned(!!provisioned[deviceId]);
            } else {
                setTlsProvisioned(null);
            }
        } catch (err: any) {
            if (!cancelledRef.current) {
                setTlsError(err?.message || 'Failed to check TLS status');
                setTlsProvisioned(null);
            }
        } finally {
            if (!cancelledRef.current) setTlsLoading(false);
        }
    }

    async function loadLifecycle(): Promise<void> {
        if (!deviceId || !window.eventsAPI?.getDeviceLifecycle) return;
        try {
            setLifecycleLoading(true);
            setLifecycleError(null);
            const res = await window.eventsAPI.getDeviceLifecycle({ deviceIds: [deviceId] });
            if (!res?.ok) {
                throw new Error(res?.error || 'Failed to load lifecycle');
            }
            if (cancelledRef.current) return;
            const data = res.data as { device?: LifecycleRow | null; devices?: LifecycleRow[] } | undefined;
            const row = data?.device || (Array.isArray(data?.devices) ? data?.devices[0] : null);
            setLifecycle(row || null);
        } catch (err: any) {
            if (!cancelledRef.current) {
                setLifecycleError(err?.message || 'Failed to load lifecycle');
                setLifecycle(null);
            }
        } finally {
            if (!cancelledRef.current) setLifecycleLoading(false);
        }
    }

    async function loadAttributes(): Promise<void> {
        if (!deviceId || !window.eventsAPI?.getDeviceAttributes) return;
        try {
            setAttributesLoading(true);
            setAttributesError(null);
            const res = await window.eventsAPI.getDeviceAttributes({ deviceId });
            if (!res?.ok) {
                throw new Error(res?.error || 'Failed to load attributes');
            }
            if (cancelledRef.current) return;
            const data = res.data as
                | { device?: { raceCount?: number; waterproofedBy?: string } | null; devices?: Array<{ raceCount?: number; waterproofedBy?: string }> }
                | undefined;
            const row = data?.device || (Array.isArray(data?.devices) ? data?.devices[0] : null);
            setAttributes(row || null);
        } catch (err: any) {
            if (!cancelledRef.current) {
                setAttributesError(err?.message || 'Failed to load attributes');
                setAttributes(null);
            }
        } finally {
            if (!cancelledRef.current) setAttributesLoading(false);
        }
    }

    async function findDeviceInEvent(eventId: string): Promise<Record<string, unknown> | null> {
        let token: string | undefined;
        for (let i = 0; i < 25; i++) {
            const res = await window.eventsAPI.listEventDevices(eventId, {
                limit: 200,
                ...(token ? { nextToken: token } : {}),
            });
            if (!res?.ok) return null;
            const data = res.data as { devices?: Array<Record<string, unknown>>; nextToken?: string | null } | undefined;
            const rows = Array.isArray(data?.devices) ? data.devices : [];
            const match = rows.find((r) => String(r?.deviceId ?? '').trim() === deviceId);
            if (match) return match;
            const next = String(data?.nextToken ?? '').trim();
            if (!next) return null;
            token = next;
        }
        return null;
    }

    async function loadAssignments(): Promise<void> {
        if (!deviceId) return;
        try {
            setAssignmentsLoading(true);
            setAssignmentsError(null);

            if (isAdmin) {
                const histFn = window.eventsAPI.queryDeviceEventAssignmentHistory;
                const [q, hq] = await Promise.all([
                    window.eventsAPI.queryDeviceEventAssignments([deviceId]),
                    typeof histFn === 'function'
                        ? histFn([deviceId])
                        : Promise.resolve({ ok: true, data: { history: [] as Record<string, unknown>[] } }),
                ]);
                if (!q?.ok) {
                    throw new Error(q?.error || 'Failed to load event assignments');
                }
                const liveRows = ((q.data as { assignments?: AssignmentDetailsRow[] } | undefined)?.assignments ?? [])
                    .map((a) => {
                        const rid = String((a as { raceId?: string })?.raceId ?? '').trim();
                        return {
                            eventId: String(a?.eventId ?? '').trim(),
                            raceId: rid.length > 0 ? rid : undefined,
                            name: a?.name,
                            status: a?.status,
                        };
                    })
                    .filter((a) => a.eventId.length > 0);

                const enrichedLive = await Promise.all(
                    liveRows.map(async (row) => {
                        const deviceRow = await findDeviceInEvent(row.eventId);
                        const fromDevice =
                            deviceRow && typeof deviceRow.raceId === 'string'
                                ? String(deviceRow.raceId).trim()
                                : '';
                        const raceId =
                            fromDevice.length > 0 ? fromDevice : row.raceId;
                        const base: AssignmentDetailsRow = {
                            ...row,
                            raceId: raceId && raceId.length > 0 ? raceId : undefined,
                            isLiveAssignment: true,
                        };
                        if (!deviceRow) return base;
                        const recalled: 0 | 1 = Number(deviceRow.recalled ?? 0) > 0 ? 1 : 0;
                        return {
                            ...base,
                            assignedAthleteId:
                                typeof deviceRow.assignedAthleteId === 'string'
                                    ? deviceRow.assignedAthleteId
                                    : undefined,
                            assignedBy: typeof deviceRow.assignedBy === 'string' ? deviceRow.assignedBy : undefined,
                            assignedAt:
                                typeof deviceRow.assignedAt === 'number'
                                    ? deviceRow.assignedAt
                                    : Number.isFinite(Number(deviceRow.assignedAt))
                                        ? Number(deviceRow.assignedAt)
                                        : undefined,
                            recalled,
                        } satisfies AssignmentDetailsRow;
                    }),
                );

                const liveKeys = new Set(
                    enrichedLive.map((r) => assignmentMergeKey(r)).filter((k) => k.length > 0),
                );

                let historyParsed: AssignmentDetailsRow[] = [];
                if (hq?.ok) {
                    const hist = (hq.data as { history?: Record<string, unknown>[] } | undefined)?.history ?? [];
                    historyParsed = hist.map(assignmentFromHistoryRecord).filter((r) => r.eventId.length > 0);
                }

                const mergedRaw = mergeAssignmentsByKey(historyParsed, enrichedLive);
                const mergedSorted = sortAssignmentsForDisplay(
                    mergedRaw.map((row) => ({
                        ...row,
                        isLiveAssignment: liveKeys.has(assignmentMergeKey(row)),
                    })),
                );
                const merged = await attachRaceMetadata(mergedSorted);
                if (!cancelledRef.current) setAssignments(merged);
                return;
            }

            if (!scopedEventId) {
                if (!cancelledRef.current) setAssignments([]);
                return;
            }

            const res = await window.eventsAPI.listEventDevices(scopedEventId, { limit: 5000 });
            if (!res?.ok) {
                throw new Error(res?.error || 'Failed to load scoped event assignment');
            }
            if (cancelledRef.current) return;
            const rows = ((res.data as { devices?: Array<Record<string, unknown>> } | undefined)?.devices ?? []);
            const match = rows.find((r) => String(r?.deviceId ?? '').trim() === deviceId);
            if (!match) {
                setAssignments([]);
                return;
            }
            const scopedRaceId =
                typeof match.raceId === 'string' && String(match.raceId).trim().length > 0
                    ? String(match.raceId).trim()
                    : undefined;
            const scopedRows: AssignmentDetailsRow[] = [
                {
                    eventId: scopedEventId,
                    raceId: scopedRaceId,
                    assignedAthleteId:
                        typeof match.assignedAthleteId === 'string' ? String(match.assignedAthleteId).trim() : undefined,
                    assignedBy: typeof match.assignedBy === 'string' ? String(match.assignedBy) : undefined,
                    assignedAt:
                        typeof match.assignedAt === 'number'
                            ? match.assignedAt
                            : Number.isFinite(Number(match.assignedAt))
                                ? Number(match.assignedAt)
                                : undefined,
                    recalled: Number(match.recalled ?? 0) > 0 ? 1 : 0,
                    isLiveAssignment: true,
                },
            ];
            const withRaceMeta = await attachRaceMetadata(scopedRows);
            setAssignments(withRaceMeta);
        } catch (err: any) {
            if (!cancelledRef.current) {
                setAssignmentsError(err?.message || 'Failed to load assignments');
                setAssignments([]);
            }
        } finally {
            if (!cancelledRef.current) setAssignmentsLoading(false);
        }
    }

    async function loadNotes(reset: boolean): Promise<void> {
        if (!deviceId || !window.eventsAPI?.listDeviceNotes) return;
        try {
            setNotesLoading(true);
            setNotesError(null);
            const res = await window.eventsAPI.listDeviceNotes({
                deviceId,
                limit: 20,
                ...(reset ? {} : notesNextToken ? { nextToken: notesNextToken } : {}),
            });
            if (!res?.ok) {
                throw new Error(res?.error || 'Failed to load notes');
            }
            if (cancelledRef.current) return;
            const data = res.data as { notes?: DeviceNoteRow[]; nextToken?: string | null } | undefined;
            const incoming = Array.isArray(data?.notes) ? data.notes : [];
            if (reset) {
                setNotes(incoming);
            } else {
                const merged = [...notes, ...incoming];
                const dedup = Array.from(new Map(merged.map((n) => [n.noteId, n])).values());
                setNotes(dedup);
            }
            const next = String(data?.nextToken ?? '').trim();
            setNotesNextToken(next || null);
        } catch (err: any) {
            if (!cancelledRef.current) {
                setNotesError(err?.message || 'Failed to load notes');
                if (reset) setNotes([]);
            }
        } finally {
            if (!cancelledRef.current) setNotesLoading(false);
        }
    }

    async function loadLogs(kind: 'race' | 'debug') {
        if (!deviceId || !window.fleetAPI?.listLogFiles) return;
        setLogsLoading(kind);
        setLogsError((prev) => ({ ...(prev || {}), [kind]: undefined }));
        try {
            const res = await window.fleetAPI.listLogFiles({
                deviceId,
                logType: kind,
                eventId: effectiveLogEventId,
            });
            if (cancelledRef.current) return;
            const files = (res || []).filter(isFileObject).sort(
                (a, b) => (b.lastModifiedEpochMs ?? 0) - (a.lastModifiedEpochMs ?? 0),
            );
            if (kind === 'race') setRaceLogs(files);
            else setDebugLogs(files);
        } catch (err: any) {
            if (cancelledRef.current) return;
            const msg = err?.message || 'Failed to load logs';
            setLogsError((prev) => ({ ...(prev || {}), [kind]: msg }));
            if (kind === 'race') setRaceLogs([]);
            else setDebugLogs([]);
        } finally {
            if (!cancelledRef.current) setLogsLoading((prev) => (prev === kind ? null : prev));
        }
    }

    async function fetchPreview(key: string) {
        if (!window.fleetAPI?.getLogPreview) return;
        setPreviewLoading(true);
        setPreviewError(null);
        setPreviewText('');
        try {
            const text = await window.fleetAPI.getLogPreview({
                key,
                mode: 'tail',
                maxBytes: 1024 * 1024,
                eventId: effectiveLogEventId,
            });
            setPreviewText(text || '');
        } catch (err: any) {
            setPreviewError(err?.message || 'Failed to load preview');
            setPreviewText('');
        } finally {
            setPreviewLoading(false);
        }
    }

    function openLog(o: S3ObjectInfo) {
        setActiveLog(o);
        setLogOpen(true);
        void fetchPreview(o.key);
    }

    async function downloadLog(o: S3ObjectInfo) {
        if (!window.fleetAPI?.downloadLogFile) return;
        await window.fleetAPI.downloadLogFile({
            key: o.key,
            suggestedName: keyBasename(o.key),
            eventId: effectiveLogEventId,
        });
    }

    async function confirmDeleteLog() {
        if (!deleteLogConfirm || !window.fleetAPI?.deleteLogFile) return;
        const { kind, obj } = deleteLogConfirm;
        setDeletingLogKey(obj.key);
        try {
            const res = await window.fleetAPI.deleteLogFile({ key: obj.key });
            if (!res?.ok) {
                window.dispatchEvent(
                    new CustomEvent('toast-error', { detail: res?.error || 'Failed to delete log file' }),
                );
                return;
            }
            if (kind === 'race') setRaceLogs((prev) => prev.filter((r) => r.key !== obj.key));
            else setDebugLogs((prev) => prev.filter((r) => r.key !== obj.key));
            if (activeLog?.key === obj.key) {
                setLogOpen(false);
                setActiveLog(null);
            }
            window.dispatchEvent(new CustomEvent('toast-info', { detail: 'Log file deleted' }));
        } finally {
            setDeletingLogKey(null);
            setDeleteLogConfirm(null);
        }
    }

    async function refreshAll() {
        await Promise.all([
            refreshCore(),
            loadTls(),
            loadLifecycle(),
            loadAttributes(),
            loadAssignments(),
            loadNotes(true),
            loadLogs('race'),
            loadLogs('debug'),
        ]);
    }

    useEffect(() => {
        if (!deviceId) return;
        cancelledRef.current = false;
        void refreshAll();
        return () => {
            cancelledRef.current = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId]);

    const scopedContextKey = `${scopedEventId ?? ''}\0${isAdmin}`;
    const scopedContextKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (!deviceId) return;
        if (scopedContextKeyRef.current === null) {
            scopedContextKeyRef.current = scopedContextKey;
            return;
        }
        if (scopedContextKeyRef.current === scopedContextKey) return;
        scopedContextKeyRef.current = scopedContextKey;
        void refreshCore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceId, scopedContextKey]);

    const provisioningLabel = tlsLoading
        ? 'Loading...'
        : tlsError
            ? 'Error'
            : tlsProvisioned == null
                ? '-'
                : tlsProvisioned
                    ? 'Provisioned'
                    : 'Unprovisioned';

    const currentAssignment = useMemo(
        () => assignments.find((a) => a.isLiveAssignment) ?? assignments[0],
        [assignments],
    );
    const lifecycleEventIdDisplay = String(lifecycle?.eventId ?? '').trim()
        || String(currentAssignment?.eventId ?? '').trim()
        || '-';
    const lifecycleRaceIdDisplay = String(lifecycle?.raceId ?? '').trim()
        || String(currentAssignment?.raceId ?? '').trim()
        || '-';
    const lifecycleRecordedAtLabel = formatApiDateTime(normalizeEpochMs(lifecycle?.recordedAt), '') || '-';
    const noteEventId =
        scopedEventId
        || String(currentAssignment?.eventId ?? '').trim();
    const lifecycleFormEventId =
        String(lifecycle?.eventId ?? '').trim()
        || String(currentAssignment?.eventId ?? '').trim()
        || scopedEventId;
    const lifecycleFormRaceId =
        String(lifecycle?.raceId ?? '').trim()
        || String(currentAssignment?.raceId ?? '').trim();
    const lifecycleSelectedIds = useMemo(
        () => (deviceId ? [deviceId] : []),
        [deviceId],
    );
    const canAddNote = !!window.eventsAPI?.addDeviceNotes;
    const canUpdateLifecycle = isAdmin && !!window.eventsAPI?.setDeviceLifecycle;

    const dossierHeaderButtonClass =
        'inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-[10px] font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

    return (
        <div className="w-full max-w-full overflow-x-hidden text-sm leading-relaxed space-y-4">
            <div className="flex items-center justify-between overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-gradient-to-r from-sky-50 via-white to-slate-50/90 dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 px-4 py-3 shadow-sm">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-slate-100 truncate flex items-center gap-2">
                        <MonitorSmartphone className="h-4 w-4 text-indigo-600" />
                        Device Dossier {deviceId ? `- ${deviceId}` : ''}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                        {isAdmin
                            ? 'Unified view of status, command, assignment, lifecycle, notes, and logs.'
                            : 'Event-scoped status and command with assignment, notes, and logs.'}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {!isAdmin ? (
                        <div className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700 dark:border-blue-700/60 dark:bg-blue-950/40 dark:text-blue-200">
                            Live event scope
                        </div>
                    ) : null}
                    {(isAdmin || !!scopedEventId) ? (
                        <RefreshButton
                            busy={coreLoading}
                            onClick={() => void refreshAll()}
                            disabled={!deviceId}
                            className="px-3 py-1.5 rounded-lg border text-xs bg-white dark:bg-slate-900 hover:bg-gray-50 disabled:opacity-60"
                            busyText="Refreshing..."
                            busyClassName="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-slate-400"
                        />
                    ) : null}
                </div>
            </div>

            {coreError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 animate-pulse dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-300">
                    {coreError}
                </div>
            ) : null}

            <DeviceStatPills
                items={[
                    { label: 'Firmware', value: fwLabel || '-', icon: 'firmware' },
                    { label: 'Modem FW', value: modemFwLabel || '-', icon: 'firmware', title: modemFwLabel || undefined },
                    { label: 'GNSS FW', value: gnssFwLabel || '-', icon: 'firmware', title: gnssFwLabel || undefined },
                    { label: 'Provisioning', value: provisioningLabel, icon: 'provisioning' },
                    { label: 'Battery', value: battLabel ? `${battLabel} V` : '-', icon: 'battery' },
                    { label: 'Race log size', value: raceLogLabel || '-', icon: 'race' },
                    { label: 'Debug log size', value: debugLogLabel || '-', icon: 'debug' },
                    { label: 'Last heard', value: lastHeardLabel || '-', icon: 'heard' },
                ]}
            />

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <DetailCard
                    title="Lifecycle"
                    right={
                        canUpdateLifecycle ? (
                            <button
                                type="button"
                                className={dossierHeaderButtonClass}
                                onClick={() => setLifecycleDialogOpen(true)}
                            >
                                Update lifecycle
                            </button>
                        ) : undefined
                    }
                >
                    {lifecycleLoading ? (
                        <CyclingLoader phrases={['Loading lifecycle status…']} size="sm" className="items-start px-0" />
                    ) : lifecycleError ? (
                        <div className="text-sm text-red-600 dark:text-red-400">{lifecycleError}</div>
                    ) : !lifecycle ? (
                        <div className="text-sm text-gray-500 dark:text-slate-400">No lifecycle data.</div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                                    Lifecycle flags
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    <LifecycleFlagPill
                                        label="Lost"
                                        active={!!lifecycle.lost}
                                        ActiveIcon={MapPinOff}
                                        InactiveIcon={MapPin}
                                        activeClass="border-rose-400 bg-rose-50 text-rose-950 dark:border-rose-700/60 dark:bg-rose-950/45 dark:text-rose-200"
                                        inactiveClass="border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-100"
                                    />
                                    <LifecycleFlagPill
                                        label="Decommissioned"
                                        active={!!lifecycle.decommissioned}
                                        ActiveIcon={Archive}
                                        InactiveIcon={ShieldCheck}
                                        activeClass="border-slate-500 bg-gray-100 text-gray-900 dark:border-slate-500 dark:bg-slate-800 dark:text-slate-100"
                                        inactiveClass="border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-100"
                                    />
                                    <LifecycleFlagPill
                                        label="Damaged"
                                        active={!!lifecycle.damaged}
                                        ActiveIcon={AlertTriangle}
                                        InactiveIcon={ShieldCheck}
                                        activeClass="border-amber-400 bg-amber-50 text-amber-950 dark:border-amber-700/60 dark:bg-amber-950/45 dark:text-amber-200"
                                        inactiveClass="border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-100"
                                    />
                                    <LifecycleFlagPill
                                        label="Errored"
                                        active={!!lifecycle.errored}
                                        ActiveIcon={AlertCircle}
                                        InactiveIcon={Check}
                                        activeClass="border-red-400 bg-red-50 text-red-950 dark:border-red-700/60 dark:bg-red-950/45 dark:text-red-200"
                                        inactiveClass="border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/40 dark:text-emerald-100"
                                    />
                                </div>
                            </div>
                            <div className="divide-y border-t border-gray-200 dark:border-slate-700 pt-3">
                                <DetailKeyValue k="Event ID" v={lifecycleEventIdDisplay} />
                                <DetailKeyValue k="Race ID" v={lifecycleRaceIdDisplay} />
                                <DetailKeyValue k="Note" v={lifecycle.reason || '-'} />
                                <DetailKeyValue k="Recorded At" v={lifecycleRecordedAtLabel} />
                                <DetailKeyValue
                                    k="Updated By"
                                    v={formatUserIdentityDisplay(lifecycle.updatedBy, userIdToDisplayName)}
                                />
                            </div>
                        </div>
                    )}
                    <div className="mt-4 border-t border-gray-200 dark:border-slate-700 pt-3">
                        <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-slate-400">
                            Attributes
                        </div>
                        {attributesLoading ? (
                            <CyclingLoader phrases={['Loading attributes…']} size="sm" className="items-start px-0" />
                        ) : (
                            <div className="divide-y">
                                <DetailKeyValue k="Race count" v={raceCountLabel || '-'} />
                                <DetailKeyValue k="Waterproofed by" v={waterproofedByLabel || '-'} />
                                <DetailKeyValue
                                    k="Updated By"
                                    v={formatUserIdentityDisplay((attributes as any)?.updatedBy, userIdToDisplayName)}
                                />
                            </div>
                        )}
                        {attributesError ? (
                            <div className="mt-1 text-xs text-red-600 dark:text-red-400">{attributesError}</div>
                        ) : null}
                    </div>
                </DetailCard>

                <DetailCard
                    title="Notes"
                    right={
                        <div className="flex flex-wrap items-center justify-end gap-2">
                            {canAddNote ? (
                                <button
                                    type="button"
                                    className={dossierHeaderButtonClass}
                                    onClick={() => setNoteDialogOpen(true)}
                                >
                                    Add note
                                </button>
                            ) : null}
                            {notesNextToken ? (
                                <button
                                    type="button"
                                    className="text-xs text-blue-600 hover:underline disabled:text-gray-400 dark:text-slate-400"
                                    onClick={() => void loadNotes(false)}
                                    disabled={notesLoading}
                                >
                                    Load more
                                </button>
                            ) : null}
                        </div>
                    }
                >
                    {notesLoading && notes.length === 0 ? (
                        <CyclingLoader phrases={['Loading device notes…']} size="sm" className="items-start px-0" />
                    ) : notesError ? (
                        <div className="text-sm text-red-600 dark:text-red-400">{notesError}</div>
                    ) : notes.length === 0 ? (
                        <div className="text-sm text-gray-500 dark:text-slate-400">No notes.</div>
                    ) : (
                        <div className="space-y-2">
                            {notes.map((n) => (
                                <div
                                    key={n.noteId}
                                    className="rounded border border-gray-200 bg-gray-50/80 px-3 py-2 dark:border-slate-700 dark:bg-slate-800/60"
                                >
                                    <div className="text-[11px] text-gray-500 dark:text-slate-400">
                                        {n.type.toUpperCase()} - {formatApiDateTime(n.createdAt, '') || '-'}
                                    </div>
                                    <div className="text-xs text-gray-900 dark:text-slate-100">{n.message}</div>
                                    <div className="text-[11px] text-gray-500 dark:text-slate-400">
                                        {formatUserIdentityDisplay(n.createdBy, userIdToDisplayName)}{' '}
                                        {n.eventId ? `| event ${n.eventId}` : ''}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </DetailCard>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <DetailCard title="Assignments">
                    {assignmentsLoading ? (
                        <CyclingLoader phrases={['Loading assignments…']} size="sm" className="items-start px-0" />
                    ) : assignmentsError ? (
                        <div className="text-sm text-red-600 dark:text-red-400">{assignmentsError}</div>
                    ) : assignments.length === 0 ? (
                        <div className="text-sm text-gray-500 dark:text-slate-400">No assignments found.</div>
                    ) : (
                        <div
                            className="max-h-[17rem] overflow-y-auto overscroll-contain space-y-3 pr-1 [scrollbar-gutter:stable]"
                            role="list"
                            aria-label="Device assignments (event and race)"
                        >
                            {assignments.map((a) => {
                                const raceStatusLabel = (a.raceStatus ?? '').trim() || '—';
                                const recalledKnown = a.recalled != null;
                                const isRecalled = a.recalled === 1;
                                const isCurrent = a.isLiveAssignment === true;
                                return (
                                    <div
                                        key={`${a.eventId}-${a.raceId ?? ''}-${a.assignedAt ?? 0}`}
                                        role="listitem"
                                        className={cls(
                                            'rounded-lg border px-3 py-2.5',
                                            isCurrent
                                                ? 'border-emerald-400 bg-emerald-50/70 dark:border-emerald-600/55 dark:bg-emerald-950/35'
                                                : 'border-gray-200 bg-gray-50 text-gray-500 dark:border-slate-700 dark:bg-slate-800/80 dark:text-slate-400',
                                        )}
                                    >
                                        {isCurrent ? (
                                            <p className="mb-1.5 text-[11px] font-medium text-emerald-900 dark:text-emerald-200">
                                                Current assignment
                                            </p>
                                        ) : null}
                                        <div
                                            className={cls(
                                                'text-xs font-semibold',
                                                isCurrent ? 'text-gray-900 dark:text-slate-100' : 'text-gray-500 dark:text-slate-400',
                                            )}
                                        >
                                            {a.name ? `${a.name} (${a.eventId})` : a.eventId}
                                        </div>
                                        <div className="mt-1.5 text-[11px] leading-snug">
                                            <span className="font-medium text-gray-500 dark:text-slate-400">Race</span>{' '}
                                            {a.raceId ? (
                                                <span className={cls(isCurrent ? 'text-gray-900 dark:text-slate-100' : 'text-gray-500 dark:text-slate-400')}>
                                                    {a.raceName ? `${a.raceName} (${a.raceId})` : a.raceId}
                                                </span>
                                            ) : (
                                                <span className="text-gray-500 dark:text-slate-400">—</span>
                                            )}
                                        </div>
                                        <div className="mt-2 flex flex-wrap items-center gap-2">
                                            {a.raceId ? (
                                                <span
                                                    className={cls(
                                                        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                                                        eventStatusBadgeClass(a.raceStatus),
                                                        !isCurrent && 'opacity-75',
                                                    )}
                                                    title="Race status"
                                                >
                                                    Race: {raceStatusLabel}
                                                </span>
                                            ) : null}
                                            {recalledKnown ? (
                                                <span
                                                    className={cls(
                                                        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold',
                                                        isCurrent &&
                                                        (isRecalled
                                                            ? 'border-emerald-300 bg-emerald-100 text-emerald-950 dark:border-emerald-700/60 dark:bg-emerald-950/50 dark:text-emerald-200'
                                                            : 'border-rose-300 bg-rose-100 text-rose-950 dark:border-rose-700/60 dark:bg-rose-950/50 dark:text-rose-200'),
                                                        !isCurrent &&
                                                        (isRecalled
                                                            ? 'border-gray-300 dark:border-slate-600 bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                                                            : 'border-gray-300 dark:border-slate-600 bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400'),
                                                    )}
                                                    title={isRecalled ? 'Device retrieved from this event' : 'Device not yet retrieved'}
                                                >
                                                    {isRecalled ? (
                                                        <Check
                                                            className={cls(
                                                                'h-3.5 w-3.5 shrink-0',
                                                                isCurrent ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-500 dark:text-slate-400',
                                                            )}
                                                            strokeWidth={2.5}
                                                            aria-hidden
                                                        />
                                                    ) : (
                                                        <X
                                                            className={cls(
                                                                'h-3.5 w-3.5 shrink-0',
                                                                isCurrent ? 'text-rose-700 dark:text-rose-300' : 'text-gray-500 dark:text-slate-400',
                                                            )}
                                                            strokeWidth={2.5}
                                                            aria-hidden
                                                        />
                                                    )}
                                                    {isRecalled ? 'Retrieved' : 'Not retrieved'}
                                                </span>
                                            ) : (
                                                <span
                                                    className={cls(
                                                        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium',
                                                        isCurrent
                                                            ? 'border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300'
                                                            : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-500 dark:text-slate-400',
                                                    )}
                                                >
                                                    Retrieval: unknown
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-2 space-y-0.5 text-[11px] text-gray-600 dark:text-slate-300">
                                            <div>
                                                <span className="font-medium text-gray-500 dark:text-slate-400">Athlete</span>{' '}
                                                <span className={isCurrent ? 'text-gray-900 dark:text-slate-100' : 'text-gray-500 dark:text-slate-400'}>
                                                    {a.assignedAthleteId || '—'}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-500 dark:text-slate-400">Assigned by</span>{' '}
                                                <span className={isCurrent ? 'text-gray-900 dark:text-slate-100' : 'text-gray-500 dark:text-slate-400'}>
                                                    {formatUserIdentityDisplay(a.assignedBy, userIdToDisplayName)}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="font-medium text-gray-500 dark:text-slate-400">Assigned at</span>{' '}
                                                <span className={isCurrent ? 'text-gray-900 dark:text-slate-100' : 'text-gray-500 dark:text-slate-400'}>
                                                    {formatApiDateTime(a.assignedAt, '') || '—'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </DetailCard>

                <DetailCard title="Latest Command">
                    {!effectiveCommand ? (
                        <div className="text-sm text-gray-500 dark:text-slate-400">No recent command.</div>
                    ) : (
                        <div className="divide-y">
                            <DetailKeyValue k="Command ID" v={effectiveCommand.ddb.commandId || '-'} />
                            <DetailKeyValue k="Type" v={effectiveCommand.ddb.type || '-'} />
                            <DetailKeyValue k="Target Version" v={effectiveCommand.ddb.targetVersion || '-'} />
                            <DetailKeyValue k="Status" v={effectiveCommand.ddb.status || '-'} />
                            <DetailKeyValue k="Updated At" v={formatApiDateTime(effectiveCommand.ddb.updatedAt, '') || '-'} />
                            {effectiveCommand.ack ? (
                                <>
                                    <DetailKeyValue k="Ack State" v={effectiveCommand.ack.state || '-'} />
                                    <DetailKeyValue k="Ack Reason" v={effectiveCommand.ack.reason || '-'} />
                                    <DetailKeyValue
                                        k="Ack Accepted"
                                        v={
                                            typeof effectiveCommand.ack.accepted === 'boolean'
                                                ? String(effectiveCommand.ack.accepted)
                                                : '-'
                                        }
                                    />
                                    <DetailKeyValue
                                        k="Ack Percent"
                                        v={
                                            effectiveCommand.ack.percent != null
                                                ? `${effectiveCommand.ack.percent}%`
                                                : '-'
                                        }
                                    />
                                </>
                            ) : null}
                        </div>
                    )}
                </DetailCard>

                <DetailCard title="Master Record">
                    <div className="divide-y max-h-64 overflow-auto">
                        {metaPairs.map(({ col, value }) => (
                            <DetailKeyValue key={col} k={deviceMappingColumnLabel(col)} v={value || '-'} />
                        ))}
                    </div>
                </DetailCard>
            </div>

            <details className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <summary className="px-4 py-3 text-sm font-semibold text-gray-900 dark:text-slate-100 cursor-pointer">Advanced Data</summary>
                <div className="px-4 pb-4 space-y-3">
                    <div>
                        <div className="text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">Raw Status</div>
                        <pre className="rounded border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-900 whitespace-pre-wrap break-words dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                            {JSON.stringify(effectiveStatus || {}, null, 2)}
                        </pre>
                    </div>
                    <div>
                        <div className="text-xs font-medium text-gray-600 dark:text-slate-400 mb-1">Raw Command</div>
                        <pre className="rounded border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-900 whitespace-pre-wrap break-words dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
                            {JSON.stringify(effectiveCommand?.ddb || {}, null, 2)}
                        </pre>
                    </div>
                </div>
            </details>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <DeviceLogsTable
                    kind="race"
                    title="Race Logs"
                    rows={raceLogs}
                    loading={logsLoading === 'race'}
                    error={logsError.race}
                    disabled={!deviceId}
                    onRefresh={() => void loadLogs('race')}
                    onOpen={openLog}
                    onDownload={(o) => void downloadLog(o)}
                    onDelete={isAdmin ? (o) => setDeleteLogConfirm({ kind: 'race', obj: o }) : undefined}
                    deletingKey={deletingLogKey}
                />
                <DeviceLogsTable
                    kind="debug"
                    title="Debug Logs"
                    rows={debugLogs}
                    loading={logsLoading === 'debug'}
                    error={logsError.debug}
                    disabled={!deviceId}
                    onRefresh={() => void loadLogs('debug')}
                    onOpen={openLog}
                    onDownload={(o) => void downloadLog(o)}
                    onDelete={isAdmin ? (o) => setDeleteLogConfirm({ kind: 'debug', obj: o }) : undefined}
                    deletingKey={deletingLogKey}
                />
            </div>

            <ConfirmDialog
                open={!!deleteLogConfirm}
                danger
                busy={!!deletingLogKey}
                title="Delete log file"
                message={
                    <>
                        Permanently delete <span className="font-mono font-semibold">{deleteLogConfirm ? keyBasename(deleteLogConfirm.obj.key) : ''}</span>?
                        This cannot be undone.
                    </>
                }
                confirmText="Delete"
                // DeviceDetails can be nested inside DeviceRosterOverlay (z-[5300]); the
                // default Dialog overlay (z-50) would render behind it, so pin above
                // every context this component can appear in (matches the z-[5300]
                // already used below for the note-taking DeviceConfigureForm dialogs).
                overlayZIndex={5310}
                onConfirm={() => void confirmDeleteLog()}
                onCancel={() => setDeleteLogConfirm(null)}
            />

            <LogPreviewDialog
                open={logOpen && !!activeLog}
                selectedKey={activeLog?.key ?? ''}
                selectedObj={activeLog ?? undefined}
                preview={previewError ? `Error: ${previewError}` : previewText}
                busyPreview={previewLoading}
                busyDownload={false}
                onReloadPreview={() => {
                    if (activeLog?.key) {
                        void fetchPreview(activeLog.key);
                    }
                }}
                onDownload={() => {
                    if (activeLog) {
                        void downloadLog(activeLog);
                    }
                }}
                onClose={() => {
                    setLogOpen(false);
                    setActiveLog(null);
                    setPreviewText('');
                    setPreviewError(null);
                }}
                // Raised above DeviceRosterOverlay (z-[5300]), which this component can be
                // nested inside; the delete-confirm ConfirmDialog above (5310) still wins
                // when both are open, since it must sit on top of everything else here.
                backdropZIndex={5301}
                dialogZIndex={5302}
            />

            {canAddNote && deviceId && noteDialogOpen ? (
                <DeviceConfigureForm
                    selectedIds={lifecycleSelectedIds}
                    initialOperation="LEAVE_DEVICE_NOTE"
                    overlayZIndexClass="z-[5300]"
                    initialNoteEventId={noteEventId}
                    onRunDeviceValidation={onRunDeviceValidation}
                    onClose={() => {
                        setNoteDialogOpen(false);
                        void loadNotes(true);
                    }}
                />
            ) : null}

            {canUpdateLifecycle && deviceId && lifecycleDialogOpen ? (
                <DeviceConfigureForm
                    selectedIds={lifecycleSelectedIds}
                    initialOperation="SUBMIT_LIFECYCLE_REPORT"
                    overlayZIndexClass="z-[5300]"
                    initialLifecycleEventId={lifecycleFormEventId}
                    initialLifecycleRaceId={lifecycleFormRaceId}
                    onRunDeviceValidation={onRunDeviceValidation}
                    onClose={() => setLifecycleDialogOpen(false)}
                    onLifecycleDirty={() => {
                        void loadLifecycle();
                        void loadAttributes();
                    }}
                />
            ) : null}
        </div>
    );
}
